import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { Button, Card, Spinner } from "@/components/ui/Primitives";
import { api, formatErr } from "@/lib/api";
import { CreditCard, CheckCircle2, XCircle, AlertTriangle, Calendar, ExternalLink, ShieldCheck } from "lucide-react";

function fmtBRL(v) {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(Number(v || 0));
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });
  } catch (_) { return iso; }
}

const STATUS_LABEL = {
  active: { label: "Ativa", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: CheckCircle2 },
  trialing: { label: "Em Teste Grátis", tone: "bg-sky-100 text-sky-800 border-sky-300", icon: ShieldCheck },
  past_due: { label: "Pagamento em atraso", tone: "bg-orange-100 text-orange-800 border-orange-300", icon: AlertTriangle },
  canceled: { label: "Cancelada", tone: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
  unpaid: { label: "Não paga", tone: "bg-red-100 text-red-800 border-red-300", icon: XCircle },
  incomplete: { label: "Incompleta", tone: "bg-orange-100 text-orange-800 border-orange-300", icon: AlertTriangle },
};

export default function Assinatura() {
  const nav = useNavigate();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/subscription/status");
      setSub(data);
    } catch (e) { setErr(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  const openPortal = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (e) { setErr(formatErr(e)); setBusy(false); }
  };

  const cancel = async () => {
    if (!window.confirm("Cancelar assinatura ao final do período atual?\n\nO acesso continua até o fim do ciclo já pago.")) return;
    setBusy(true); setErr("");
    try {
      await api.post("/subscription/cancel", { origin_url: window.location.origin });
      await load();
    } catch (e) { setErr(formatErr(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Layout title="Assinatura"><div className="flex justify-center py-20"><Spinner size={28} /></div></Layout>;

  const st = STATUS_LABEL[sub?.status] || { label: sub?.status ? sub.status : "Sem assinatura", tone: "bg-stone-100 text-ink-700 border-ink-200", icon: CreditCard };
  const StatusIcon = st.icon;
  const hasSub = !!sub?.subscription_id;
  const isActive = sub?.is_active;
  const willCancel = sub?.cancel_at_period_end;

  return (
    <Layout
      title="Assinatura"
      subtitle="Gestão do seu plano Serenata ERP"
      actions={<Button variant="secondary" onClick={() => nav("/settings")}>Voltar às Configurações</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Card principal — status */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.1em] text-ink-500 mb-1">Status atual</div>
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-bold ${st.tone}`} data-testid="sub-status-badge">
                <StatusIcon size={16} /> {st.label}
              </div>
              {willCancel && (
                <div className="mt-2 text-xs text-orange-600">
                  ⚠ Cancelamento agendado para {fmtDate(sub.current_period_end)}
                </div>
              )}
              {!isActive && sub?.reason && (
                <div className="mt-2 text-xs text-danger">
                  Motivo: <strong>{sub.reason}</strong>
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-xs text-ink-500">Mensalidade</div>
              <div className="font-mono text-3xl font-bold text-brand-500" data-testid="sub-amount">{fmtBRL(sub?.amount_brl)}</div>
              <div className="text-xs text-ink-500">/ mês</div>
            </div>
          </div>

          {hasSub && (
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {sub?.trial_end && new Date(sub.trial_end) > new Date() && (
                <div className="p-3 rounded-lg bg-sky-50 border border-sky-200">
                  <div className="text-xs text-sky-700 uppercase font-bold">Teste grátis até</div>
                  <div className="font-mono mt-0.5 flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(sub.trial_end)}</div>
                </div>
              )}
              {sub?.current_period_end && (
                <div className="p-3 rounded-lg bg-stone-50 border border-ink-200">
                  <div className="text-xs text-ink-500 uppercase font-bold">Próximo vencimento</div>
                  <div className="font-mono mt-0.5 flex items-center gap-1.5"><Calendar size={14} /> {fmtDate(sub.current_period_end)}</div>
                </div>
              )}
            </div>
          )}

          {err && <div className="mt-4 text-sm text-danger bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}

          <div className="mt-6 flex flex-col sm:flex-row gap-2">
            {!hasSub || !isActive ? (
              <Button onClick={activate} disabled={busy} data-testid="activate-sub-btn" className="sm:w-auto w-full">
                {busy ? <Spinner size={16} /> : <><CreditCard size={16} /> Ativar Assinatura — R$ 350/mês</>}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={openPortal} disabled={busy} data-testid="portal-sub-btn">
                  <ExternalLink size={16} /> Atualizar cartão / Histórico
                </Button>
                {!willCancel && (
                  <Button variant="secondary" onClick={cancel} disabled={busy} data-testid="cancel-sub-btn" className="text-danger">
                    Cancelar assinatura
                  </Button>
                )}
              </>
            )}
          </div>
        </Card>

        {/* Card lateral — o que inclui */}
        <Card className="p-6">
          <h3 className="font-heading font-bold text-ink-900 mb-3">O que está incluído</h3>
          <ul className="space-y-2 text-sm text-ink-700">
            {[
              "Controle completo de estoque de cebolas",
              "Beneficiamento & Classificação",
              "Emissão de Romaneios de Saída",
              "Bipagem QR Code na Expedição",
              "Emissão de NF-e via Focus NF-e",
              "Módulo Financeiro (contas, fluxo de caixa)",
              "Módulo de RH (folha, ponto biométrico)",
              "Beneficiamento a Terceiros",
              "Backup & Restore completo",
              "Suporte via chat da Emergent",
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 size={16} className="text-brand-500 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-4 border-t border-tertiary text-xs text-ink-500">
            Teste grátis de 7 dias no primeiro cadastro. Cancele quando quiser.
          </div>
        </Card>
      </div>
    </Layout>
  );
}
